// Two-tier section navigation, rendered once inside PageShell so it appears
// above the title on every route. Ported from the TB-EMS SPA's ViewTabs.
//
//  - Tier 1 — mode pills (Monitor / Manage / Analyze). The Operator-SPA has
//    a write/config cluster that TB-EMS lacks, hence three tiers not two.
//  - Tier 2 — sub-tabs of the active mode, underlined in the electric-blue accent.
//    Below `md` they collapse into a "More views" dropdown.
//
// The Alarms-tab count badge is driven by the shared `useAlarms()`
// react-query cache (one fetch SPA-wide, see useAlarms.ts); the mode-pill
// dot and the pre-data fallback still key off `snap.health.overall`.
// `?mode=` deep-linking is untouched: ViewTabs is a pure function of
// `pathname`, read after App's ModeRouter resolves.
//
// Per-tab last-location memory (NAV-02): every location change is recorded
// in sessionStorage under its owning tab, and every tab link (tier-2 strip,
// mode pills, pill dropdowns, mobile menu) returns to that remembered URL.
// Clicking the ALREADY-ACTIVE tab resets to the tab root — the escape
// hatch out of a deep drill-down.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { useSnapshot } from "@/store/site";
import { useAlarms } from "@/hooks/useAlarms";
import { carriedSearch } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import {
  MODES,
  MONITOR,
  NAV_LABEL_KEY,
  isActivePath,
  type ModeDef,
  type TabDef,
} from "@/lib/navTree";
import { rememberTabLocation, rememberedTabLocation } from "@/lib/tabMemory";
import type { Alarm } from "@/data/types";

// The nav tree (MODES / MONITOR / MANAGE / ANALYZE), NAV_LABEL_KEY and
// isActivePath now live in lib/navTree.ts (the single chord/route source);
// the per-tab last-location memory lives in lib/tabMemory.ts. Both are
// imported above. ViewTabs renders the same tree, unchanged.

// Worst-first severity order + count-badge tints (complete static class
// strings — Tailwind can't see runtime-composed names). Same --ems-alarm-*
// tokens as lib/severity.ts (badge variant: soft fill + text, no border).
const SEV_ORDER: Alarm["severity"][] = ["critical", "major", "minor", "warning"];

const BADGE_BY_SEV: Record<Alarm["severity"], string> = {
  critical: "bg-critical/15 text-critical",
  major: "bg-[var(--ems-alarm-major)]/15 text-[var(--ems-alarm-major)]",
  minor: "bg-[var(--ems-alarm-minor)]/15 text-[var(--ems-alarm-minor)]",
  warning: "bg-[var(--ems-alarm-warning)]/15 text-[var(--ems-alarm-warning)]",
};

// Cross-route forwarding now lives in lib/carryParams (carriedSearch) so
// tab links, the palette, g-chords and tile links can never disagree about
// which context survives a navigation (NAV-04/NAV-07).

export function ViewTabs() {
  const { pathname, search: locationSearch } = useLocation();
  const [sp] = useSearchParams();
  const snap = useSnapshot();
  const t = useT();
  const navLabel = (label: string) => t(NAV_LABEL_KEY[label] ?? label);
  const overall = snap?.health?.overall ?? "ok";
  const alarmActive = overall === "warn" || overall === "critical";

  // Shared alarms cache — the same query every other consumer mounts, so
  // this adds no extra fetch (see useAlarms.ts).
  const { data: alarms } = useAlarms();
  const activeAlarms = (alarms ?? []).filter((a) => a.status === "active");
  const activeCount = activeAlarms.length;
  const criticalCount = activeAlarms.filter((a) => a.severity === "critical").length;
  const worstSev = SEV_ORDER.find((s) => activeAlarms.some((a) => a.severity === s));
  const alarmTitle = activeCount > 0
    ? t(activeCount === 1 ? "monitor.nav.activeAlarms.one" : "monitor.nav.activeAlarms.other", { n: activeCount })
      + (criticalCount > 0 ? ` ${t("monitor.nav.nCritical", { n: criticalCount })}` : "")
    : t("monitor.nav.siteHealth", { state: overall });

  const moreRef = useRef<HTMLDivElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  // Desktop discoverability (NAV-05): each INACTIVE mode pill carries a
  // click-opened dropdown listing that mode's tabs — same markup/dismissal
  // pattern as the mobile "More views" menu below.
  const pillsRef = useRef<HTMLDivElement>(null);
  const [openModeId, setOpenModeId] = useState<ModeDef["id"] | null>(null);

  const activeMode = useMemo(
    () => MODES.find((m) => m.tabs.some((t) => isActivePath(t, pathname))) ?? MONITOR,
    [pathname],
  );
  const activeTab = activeMode.tabs.find((t) => isActivePath(t, pathname));
  const search = carriedSearch(sp);

  // Record the current location under its owning tab so tab links can
  // return the operator to exactly where they left off (NAV-02).
  useEffect(() => {
    rememberTabLocation(pathname, locationSearch);
  }, [pathname, locationSearch]);

  // Tab-link destination: the remembered in-tab location when one exists,
  // the tab root + carried context otherwise. The ALREADY-ACTIVE tab always
  // links to its root — clicking it is the deliberate reset.
  const tabHref = (tab: TabDef): string => {
    if (isActivePath(tab, pathname)) return tab.path + search;
    return rememberedTabLocation(tab.path) ?? tab.path + search;
  };

  // Mode pills land on the mode's default tab, with the same memory.
  const modeHref = (m: ModeDef): string => {
    const landing = m.tabs.find((t) => t.path === m.defaultPath);
    return landing ? tabHref(landing) : m.defaultPath + search;
  };

  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    // setTimeout (not rAF) defers attachment past the opening click while
    // still firing in a backgrounded tab — rAF would never fire there.
    const timer = window.setTimeout(() => document.addEventListener("click", onClick), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  // Same Esc/outside-click dismissal for the mode-pill dropdowns.
  useEffect(() => {
    if (!openModeId) return;
    const onClick = (e: MouseEvent) => {
      if (pillsRef.current && !pillsRef.current.contains(e.target as Node)) setOpenModeId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenModeId(null);
    };
    const timer = window.setTimeout(() => document.addEventListener("click", onClick), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openModeId]);

  // Any navigation closes an open pill dropdown (e.g. clicking another
  // pill's label while a dropdown is open).
  useEffect(() => {
    setOpenModeId(null);
  }, [pathname]);

  const alarmDot = (
    <span
      role="img"
      title={alarmTitle}
      aria-label={alarmTitle}
      className={cn(
        "ml-1 inline-block h-1.5 w-1.5 rounded-full",
        overall === "critical" ? "bg-critical" : "bg-warn",
      )}
    />
  );

  // Count badge on the Alarms tab. Falls back to the plain dot until the
  // first alarms response lands — a badge with a made-up count would be
  // worse than no number at all.
  const alarmBadge = alarms
    ? activeCount > 0 && worstSev
      ? (
        <span
          role="img"
          title={alarmTitle}
          aria-label={alarmTitle}
          className={cn(
            "ml-1 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none num",
            BADGE_BY_SEV[worstSev],
          )}
        >
          {activeCount}
        </span>
      )
      : null
    : alarmActive
      ? alarmDot
      : null;

  return (
    <nav aria-label={t("monitor.nav.sectionNav")} className="mb-5">
      {/* Tier 1 — mode pills */}
      <div ref={pillsRef} className="mb-2 flex items-center gap-2">
        {MODES.map((m) => {
          const active = m.id === activeMode.id;
          if (active) {
            // Active pill is a state indicator, NOT a link — an accidental
            // click must not teleport away from the current page (NAV-10).
            // Ink-coloured fill; text uses --ems-surface so it inverts with
            // the fill in dark mode (--ems-ink goes light there).
            return (
              <span
                key={m.id}
                aria-current="page"
                className="rounded-md px-3.5 py-1.5 text-sm font-semibold shadow-card transition-all duration-150"
                style={{ background: "var(--ems-ink)", color: "var(--ems-surface)" }}
              >
                {navLabel(m.label)}
              </span>
            );
          }
          const menuOpen = openModeId === m.id;
          return (
            <div key={m.id} className="relative">
              <span
                className="flex items-center rounded-md border border-edge bg-surface text-sm font-semibold text-text-muted transition-all duration-150 hover:border-edge-strong hover:bg-surface-2 hover:text-text"
                title={`${navLabel(m.label)} — ${t(m.intentKey)} · ${m.tabs.map((tb) => navLabel(tb.label)).join(" · ")}`}
              >
                <Link to={modeHref(m)} className="rounded-l-md py-1.5 pl-3.5 pr-1">
                  {navLabel(m.label)}
                  {m.id === "monitor" && alarmActive && alarmDot}
                </Link>
                <button
                  type="button"
                  onClick={() => setOpenModeId((v) => (v === m.id ? null : m.id))}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label={t("monitor.nav.modeViews", { mode: navLabel(m.label) })}
                  className="self-stretch rounded-r-md py-1.5 pl-0.5 pr-2"
                >
                  <ChevronDown
                    size={12}
                    strokeWidth={2}
                    className="transition-transform"
                    style={{ transform: menuOpen ? "rotate(180deg)" : "none" }}
                    aria-hidden
                  />
                </button>
              </span>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute left-0 top-full z-40 mt-1 min-w-[210px] rounded-lg border border-edge bg-surface p-1.5 shadow-xl"
                >
                  <div className="px-3 pt-1 text-[10px] font-semibold uppercase tracking-wider text-text-faint">
                    {navLabel(m.label)}
                  </div>
                  <div className="px-3 pb-1 text-[11px] leading-snug text-text-faint">
                    {t(m.intentKey)}
                  </div>
                  {m.tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <Link
                        key={tab.path}
                        to={tabHref(tab)}
                        role="menuitem"
                        onClick={() => setOpenModeId(null)}
                        className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-text-muted hover:bg-surface-2"
                      >
                        <Icon size={14} strokeWidth={1.75} aria-hidden />
                        {navLabel(tab.label)}
                        {tab.showsAlarmBadge && alarmBadge}
                      </Link>
                    );
                  })}
                  {/* Discovery affordance (NAV-26): alarm-RULE authoring is a
                      sub-view of the /alarms hub, not a Manage tab — but this
                      dropdown promises "schedules, rules, overrides", so surface
                      a direct hop here for the one persona who hunts for it.
                      Dropdown-ONLY (never a TabDef): the tier-2 strip and the
                      isActivePath/tabMemory matcher stay untouched. The trailing
                      "→ Alarms" cue signals that /alarms/rules resolves to
                      Monitor mode, so the Manage pill flipping isn't a surprise. */}
                  {m.id === "manage" && (
                    <Link
                      to={rememberedTabLocation("/alarms/rules") ?? "/alarms/rules"}
                      role="menuitem"
                      onClick={() => setOpenModeId(null)}
                      className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-text-muted hover:bg-surface-2"
                    >
                      <AlertTriangle size={14} strokeWidth={1.75} aria-hidden />
                      {navLabel("Alarm rules")}
                      <span className="ml-auto text-[10px] text-text-faint">→ {navLabel("Alarms")}</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Active-mode intent caption (NAV-05): one muted line that makes the
          3-mode model self-describing where the user already looks. Reuses the
          mode's intentKey copy verbatim (no new strings). Desktop-only — on the
          narrow TB iframe the long de/it strings would wrap and steal the
          scarce vertical space the tier-2 "More views" row needs; let it wrap
          (no nowrap) so a theme/zoom never clips it. */}
      <div className="mb-2 hidden text-[11px] leading-snug text-text-faint md:block">
        {t(activeMode.intentKey)}
      </div>

      {/* Tier 2 — sub-tabs of the active mode */}
      <div className="border-b border-edge">
        {/* md+ : inline tab strip */}
        <div className="hidden items-end gap-0 overflow-x-auto md:flex">
          {activeMode.tabs.map((tab) => {
            const active = isActivePath(tab, pathname);
            const Icon = tab.icon;
            return (
              <Link
                key={tab.path}
                to={tabHref(tab)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2 text-sm font-medium transition-colors",
                  active
                    ? "text-text after:absolute after:inset-x-0 after:bottom-0 after:h-[2.5px] after:rounded-t after:bg-[var(--ems-brand)]"
                    : "text-text-faint hover:bg-surface-2 hover:text-text-muted",
                )}
              >
                <Icon
                  size={14}
                  strokeWidth={1.75}
                  className={active ? "opacity-100" : "opacity-70"}
                  aria-hidden
                />
                {navLabel(tab.label)}
                {tab.showsAlarmBadge && alarmBadge}
              </Link>
            );
          })}
        </div>

        {/* < md : active tab + a "More views" dropdown */}
        <div ref={moreRef} className="relative flex w-full items-end gap-0 md:hidden">
          {activeTab && (
            <span
              aria-current="page"
              className="relative inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm font-medium text-text after:absolute after:inset-x-0 after:bottom-0 after:h-[2.5px] after:rounded-t after:bg-[var(--ems-brand)]"
            >
              <activeTab.icon size={14} strokeWidth={1.75} aria-hidden />
              {navLabel(activeTab.label)}
              {activeTab.showsAlarmBadge && alarmBadge}
            </span>
          )}
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            className="ml-auto inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-text-muted transition-colors hover:text-text"
          >
            {t("monitor.nav.moreViews")}
            <ChevronDown
              size={14}
              strokeWidth={2}
              className="transition-transform"
              style={{ transform: moreOpen ? "rotate(180deg)" : "none" }}
              aria-hidden
            />
          </button>
          {moreOpen && (
            <div
              role="menu"
              className="absolute right-2 top-full z-40 mt-1 min-w-[210px] rounded-lg border border-edge bg-surface p-1.5 shadow-xl"
            >
              {MODES.map((m) => (
                <div key={m.id} className="py-0.5">
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-text-faint">
                    {navLabel(m.label)}
                  </div>
                  {m.tabs.map((tab) => {
                    const active = isActivePath(tab, pathname);
                    const Icon = tab.icon;
                    return (
                      <Link
                        key={tab.path}
                        to={tabHref(tab)}
                        role="menuitem"
                        onClick={() => setMoreOpen(false)}
                        className={cn(
                          "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm",
                          active
                            ? "bg-accent/10 font-semibold text-accent"
                            : "text-text-muted hover:bg-surface-2",
                        )}
                      >
                        <Icon size={14} strokeWidth={1.75} aria-hidden />
                        {navLabel(tab.label)}
                        {tab.showsAlarmBadge && alarmBadge}
                      </Link>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
